import * as path from 'path'
import * as fs from 'fs'

const { dedup, resample, prune } = require('@gltf-transform/functions')
import { NodeIO, Document } from '@gltf-transform/core'
import draco3d from 'draco3dgltf'
import { MaterialsUnlit } from '@gltf-transform/extensions'
const { DracoMeshCompression, KHRONOS_EXTENSIONS } = require('@gltf-transform/extensions')

import { wrapper, TypeOnEveryFile, TypeToolProp, TypeToolOptions } from '@asset-toolkit/core'

/**
 * ### A tool to transform gltf/glb files.
 * This tool supports reading, editing, and writing 3D models in glTF 2.0 format
 *
 *  https://gltf-transform.donmccurdy.com/
 * 
 * ### Example
 * 
 *```js
  const { pipeline } = require("@asset-toolkit/core");
  const gltfTransform = require("@asset-toolkit/gltf-transform");
 *```
 *
 * ```js
  const pipelineGTLF = pipeline(gltfTransform);
  pipelineGTLF({
      pathIn: "./gltf/",
      pathOut: "../public/gltf",
  });
 * ```
 *
 * ### Useful Example
 * In combination with image-supercompression tool to transform all images attached (supercompressed ktx2 image).
 * Use cloneFiles tool utility to copy all element from origin folder to output folder.
 * Passing the options to gltf-transform to activate the basis extension.
 * 
 *```js
  const { pipeline, cloneFiles } = require("@asset-toolkit/core");
  const gltfTransform = require("@asset-toolkit/gltf-transform");
 *```
 * 
 * ```js
  const pipelineGTLF = pipeline(
    imageSuperCompression,
    (props) => cloneFiles({ pathIn: "./gltf/", ...props }),
    gltfTransform
  );

  pipelineGTLF(
    {
      pathIn: "./gltf/",
      pathOut: "../public/gltf",
    },
    [, , { basis: true }]
  );
 * 
 * @module
 */
function toBuffer(ab: ArrayBuffer) {
  const buf = Buffer.alloc(ab.byteLength)
  const view = new Uint8Array(ab)
  for (let i = 0; i < buf.length; ++i) {
    buf[i] = view[i]
  }
  return buf
}

export interface TypeProps extends TypeToolOptions {
  draco?: number | boolean
  unlit?: boolean
  applyBasis?: boolean
  optimize?: boolean
  save?: boolean
  saveJSON?: boolean
  saveBinary?: boolean
  extractTextures?: boolean
  removeTextures?: boolean
  removeMaterials?: boolean
  callback?: (a: Document, b: string) => void
}

/**
 * A tool to minify the json file.
 * @param props - { pathIn, pathOut }
 * @param options - { }
 * @returns { Promise(returns) }
 */

const gltfTransform: (_props: TypeToolProp, options?: TypeProps) => Promise<unknown> = wrapper(
  ({ pathIn, pathOut, options, file, next, wrapperProps, log }: TypeOnEveryFile) => {
    const process = async () => {
      const io = new NodeIO().registerExtensions([DracoMeshCompression, ...KHRONOS_EXTENSIONS]).registerDependencies({
        'draco3d.decoder': await draco3d.createDecoderModule(),
        'draco3d.encoder': await draco3d.createEncoderModule(),
      })

      const subProcess = async () => {
        const opts = {
          save: true,
          ...options,
        }

        const { extractTextures, removeTextures, removeMaterials, applyBasis, optimize, draco, callback, save, saveJSON, saveBinary, unlit } = opts as TypeProps

        const doc = io.read(path.resolve(pathIn || pathOut, file))

     
        let isUnlit

         doc
           .getRoot()
           .listExtensionsUsed()
           .map(e => {
             if (e.extensionName === 'KHR_materials_unlit') {
               isUnlit = true
             }
           })

        if (unlit || isUnlit) {
          const unlitExtension = doc.createExtension(MaterialsUnlit)
          doc
          .getRoot()
          .listMaterials()
          .map(material => {
            material.getName()
            material.setExtension('KHR_materials_unlit', unlitExtension.createUnlit())
          })
        }

        if (applyBasis) {
          const { TextureBasisu } = require('@gltf-transform/extensions')
          const basisuExtension = doc.createExtension(TextureBasisu).setRequired(true)

          doc
            .getRoot()
            .listTextures()
            .map((texture, key) => {
              const ext = texture.getMimeType() === 'image/png' ? 'png' : 'jpg'
              let name = texture.getName()

              if (!name) texture.setName(`image_${key}`)
              const file = `${name}.${ext}.ktx2`
              let pathCurrent = pathOut

              if (pathIn && fs.existsSync(path.resolve(pathIn, file))) {
                pathCurrent = pathIn
              }

              if (fs.existsSync(path.resolve(pathCurrent, file))) {
                const ktx2Image = fs.readFileSync(path.resolve(pathCurrent, file))
                texture.setMimeType('image/ktx2').setImage(ktx2Image)
                fs.rmSync(path.resolve(pathCurrent, file))
              }

              return texture
            })
        } else {
          doc
            .getRoot()
            .listTextures()
            .map((texture, key) => {
              const ext = texture.getMimeType() === 'image/png' ? 'png' : 'jpg'
              const name = texture.getName()
              const file = `${name}.${ext}`

              if (fs.existsSync(path.resolve(pathOut, file))) {
                texture.setMimeType(texture.getMimeType()).setImage(fs.readFileSync(path.resolve(pathOut, file)))
                fs.rmSync(path.resolve(pathOut, file))
              }
              return texture
            })
        }

        if (extractTextures) {
          doc
            .getRoot()
            .listTextures()
            .map((texture, key) => {
              const ext = texture.getMimeType() === 'image/png' ? 'png' : 'jpg'
              const image = texture.getImage()
              let name = texture.getName()
              if (!name) texture.setName(`image_${key}`)

              log(`${texture.getName()}.${ext}`)

              if (image) {
                fs.writeFileSync(path.resolve(pathOut, `${texture.getName()}.${ext}`), toBuffer(image))
              }

              return texture
            })
        }

        if (removeMaterials) {
          doc
          .getRoot()
          .listMaterials()
          .map((material, key) => {
            return material.dispose()
          })
        }


        if (removeTextures) {
          doc
          .getRoot()
          .listTextures()
          .map((texture, key) => {
            return texture.dispose()
          })
        }

        const ext = path.extname(file)

        if (optimize) {
          await doc.transform(resample(), prune())
        }

        if (draco) {
          doc
            .createExtension(DracoMeshCompression)
            .setRequired(true)
            //@ts-ignore
            .setEncoderOptions({
              method: DracoMeshCompression.EncoderMethod.EDGEBREAKER,
              encodeSpeed: typeof draco === 'boolean' ? 5 : draco,
              decodeSpeed: 5,
            })
        }

        if (callback) callback(doc, file)

        if (save) {
          io.write(path.resolve(pathOut, file.replace(ext, '.glb')), doc)
        }

        if (saveJSON) {
          fs.writeFileSync(path.resolve(pathOut, file.replace(ext, '.json')), JSON.stringify(io.writeJSON(doc)))
        }

        if (saveBinary) {
          fs.writeFileSync(path.resolve(pathOut, file.replace(ext, '.bin')), Buffer.from(io.writeBinary(doc)))
        }

        

        next()
      }

      subProcess()
    }

    process()
  },
  undefined,
  { exts: ['gltf', 'glb'], name: 'GLTF Transform', draco3dDecoder: null, draco3dEncoder: null },
)

export default gltfTransform
